/**
 * SEO Dashboard — Iter 213 Phase 1.
 *
 * Renders the SEO health of a single Brand:
 *   • Health score gauge · issues count · pages crawled
 *   • Top issue kinds (with color-coded severity)
 *   • Worst-scoring pages
 *   • PageSpeed Insights snapshot (mobile + desktop)
 *   • Audit runs history + "Run new audit" button
 *
 * Mounted under Brand detail as the "SEO" tab, and also usable from the
 * dedicated workspace-level /app/seo page.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, RefreshCcw, Zap, Globe, AlertTriangle, TrendingUp, ListChecks, Play, Loader2, Settings2, Download, Mail, Link2, Key } from "lucide-react";
import { fmtRelative } from "@/lib/format";

const SEVERITY_STYLE = {
  critical: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30",
  high:     "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",
  medium:   "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  low:      "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30",
};

export default function SEODashboard({ workspaceId, brand, testid = "seo" }) {
  const [prop, setProp] = useState(null);
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState(true);
  const [running, setRunning] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(false);

  const bid = brand?.id;

  const load = useCallback(async () => {
    if (!workspaceId || !bid) return;
    setBusy(true);
    try {
      const { data: p } = await api.get(`/workspaces/${workspaceId}/brands/${bid}/seo-property`);
      setProp(p);
      if (p) {
        const { data: d } = await api.get(`/seo-properties/${p.id}/dashboard`);
        setDash(d);
      } else {
        setDash(null);
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load SEO data")); }
    finally { setBusy(false); }
  }, [workspaceId, bid]);
  useEffect(() => { load(); }, [load]);

  const runAudit = async () => {
    if (!prop) return;
    setRunning(true);
    try {
      await api.post(`/seo-properties/${prop.id}/audit`, { max_pages: 50 });
      toast.success("Audit queued — refresh in ~30s");
      // Poll for completion (every 5s, up to 90s)
      for (let i = 0; i < 18; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data } = await api.get(`/seo-properties/${prop.id}/audit/runs`);
        const latest = data?.[0];
        if (latest?.status === "done" || latest?.status === "failed") {
          await load();
          break;
        }
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Audit failed to start")); }
    finally { setRunning(false); }
  };

  if (busy) return <div className="p-6 text-sm text-muted-foreground">Loading SEO data…</div>;

  if (!prop) {
    return (
      <>
        <div className="border border-dashed border-border p-8 text-center space-y-4" data-testid={`${testid}-empty`}>
          <Globe className="w-10 h-10 mx-auto text-muted-foreground" />
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "var(--font-heading)" }}>Attach this brand&apos;s website</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Add the root URL, target country + keywords to begin tracking SEO health,
              running technical audits, and (soon) rank tracking.
            </p>
          </div>
          <Button onClick={() => setSetupOpen(true)} className="rounded-none" data-testid={`${testid}-setup-btn`}>
            <Settings2 className="w-4 h-4 mr-2" /> Set up SEO
          </Button>
        </div>
        {setupOpen && (
          <PropertySetupDialog
            workspaceId={workspaceId}
            brand={brand}
            existing={null}
            onClose={() => setSetupOpen(false)}
            onSaved={async () => { setSetupOpen(false); await load(); }}
          />
        )}
      </>
    );
  }

  const score = dash?.property?.health_score ?? prop.health_score ?? null;
  const latest = dash?.latest_run;
  const topIssues = dash?.top_issues || [];
  const worstPages = dash?.worst_pages || [];
  const history = dash?.history || [];
  const psMobile = dash?.pagespeed?.mobile;
  const psDesktop = dash?.pagespeed?.desktop;

  return (
    <div className="space-y-6" data-testid={`${testid}-dashboard`}>
      {/* Header */}
      <div className="border border-border p-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Globe className="w-5 h-5 text-emerald-500" />
          <a href={prop.root_url} target="_blank" rel="noopener noreferrer"
             className="text-lg font-semibold hover:underline min-w-0 truncate"
             data-testid={`${testid}-root-url`}>{prop.root_url}</a>
          <span className="chip text-[10px]">{prop.country?.toUpperCase() || "IN"} · {prop.language || "en"}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-none" onClick={() => setSetupOpen(true)} data-testid={`${testid}-edit-btn`}>
            <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Edit
          </Button>
          {latest && (
            <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/seo-audit-runs/${latest.id}/pdf`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center h-8 px-3 border border-input text-xs hover:bg-muted rounded-none"
                 data-testid={`${testid}-download-pdf`}>
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
            </a>
          )}
          <Button size="sm" className="rounded-none" onClick={runAudit} disabled={running} data-testid={`${testid}-run-audit-btn`}>
            {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
            {running ? "Auditing…" : "Run audit"}
          </Button>
        </div>
      </div>

      {/* Sub-tabs */}
      <SubTabs prop={prop} testid={testid} />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={TrendingUp} label="Health score" value={score != null ? `${score}/100` : "—"}
                 accent={score == null ? "text-muted-foreground" : score >= 80 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-red-600"}
                 testid={`${testid}-kpi-health`} />
        <KpiCard icon={AlertTriangle} label="Open issues" value={latest?.issues_count ?? 0}
                 accent={latest?.issues_count > 0 ? "text-orange-600" : "text-emerald-600"}
                 testid={`${testid}-kpi-issues`} />
        <KpiCard icon={ListChecks} label="Pages crawled" value={latest?.pages_crawled ?? 0}
                 testid={`${testid}-kpi-pages`} />
        <KpiCard icon={RefreshCcw} label="Last audit" value={latest?.finished_at ? fmtRelative(latest.finished_at) : "Never"}
                 accent={latest ? "text-emerald-600" : "text-muted-foreground"}
                 testid={`${testid}-kpi-last`} />
      </div>

      {/* Content — 2 col */}
      <div className="grid md:grid-cols-2 gap-4">
        <section className="border border-border p-4" data-testid={`${testid}-top-issues`}>
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow">Top issue kinds</div>
            {latest && (
              <button onClick={() => setIssuesOpen(true)} className="text-xs text-primary hover:underline" data-testid={`${testid}-view-all-issues`}>
                View all →
              </button>
            )}
          </div>
          {topIssues.length === 0 && <div className="text-xs text-muted-foreground">Nothing to fix — great work.</div>}
          <div className="space-y-1.5">
            {topIssues.map((iss, i) => (
              <div key={i} className="flex items-center gap-2 border border-border p-2 text-xs">
                <span className={`chip text-[9px] border ${SEVERITY_STYLE[iss.severity] || ""}`}>{iss.severity}</span>
                <span className="flex-1 truncate font-medium">{iss.kind.replace(/_/g, " ")}</span>
                <span className="tabular-nums font-semibold">{iss.count}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="border border-border p-4" data-testid={`${testid}-worst-pages`}>
          <div className="label-eyebrow mb-3">Worst-scoring pages</div>
          {worstPages.length === 0 && <div className="text-xs text-muted-foreground">No pages crawled yet.</div>}
          <div className="space-y-1.5">
            {worstPages.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-2 border border-border p-2 text-xs hover:border-primary/40">
                <div className={`w-10 h-10 shrink-0 border ${p.health_score >= 80 ? "border-emerald-500 text-emerald-600" : p.health_score >= 50 ? "border-amber-500 text-amber-600" : "border-red-500 text-red-600"} font-bold flex items-center justify-center`}>
                  {p.health_score}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{p.title || p.url}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{p.url}</div>
                </div>
                <span className="chip text-[9px]">{p.issue_count} issue{p.issue_count === 1 ? "" : "s"}</span>
              </a>
            ))}
          </div>
        </section>

        <section className="border border-border p-4" data-testid={`${testid}-pagespeed`}>
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow inline-flex items-center gap-1"><Zap className="w-3 h-3" /> PageSpeed Insights</div>
            <RunPageSpeed prop={prop} onDone={load} testid={testid} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <PageSpeedCard label="Mobile" data={psMobile} />
            <PageSpeedCard label="Desktop" data={psDesktop} />
          </div>
        </section>

        <section className="border border-border p-4" data-testid={`${testid}-history`}>
          <div className="label-eyebrow mb-3">Audit history</div>
          {history.length === 0 && <div className="text-xs text-muted-foreground">No completed audits yet.</div>}
          <div className="space-y-1.5">
            {history.map((r) => (
              <div key={r.id} className="flex items-center gap-2 border border-border p-2 text-xs">
                <div className={`w-8 h-8 shrink-0 border ${r.health_score >= 80 ? "border-emerald-500 text-emerald-600" : r.health_score >= 50 ? "border-amber-500 text-amber-600" : "border-red-500 text-red-600"} font-bold flex items-center justify-center text-[11px]`}>
                  {r.health_score}
                </div>
                <div className="min-w-0 flex-1">
                  <div>{r.pages_crawled} pages · {r.issues_count} issues</div>
                  <div className="text-[10px] text-muted-foreground">{fmtRelative(r.started_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {setupOpen && (
        <PropertySetupDialog
          workspaceId={workspaceId}
          brand={brand}
          existing={prop}
          onClose={() => setSetupOpen(false)}
          onSaved={async () => { setSetupOpen(false); await load(); }}
        />
      )}
      {issuesOpen && (
        <AllIssuesDialog run={latest} onClose={() => setIssuesOpen(false)} testid={testid} />
      )}
    </div>
  );
}

/* ── KPI card ─────────────────────────────────────────── */
function KpiCard({ icon: Icon, label, value, accent = "text-foreground", testid }) {
  return (
    <div className="border border-border p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 label-eyebrow text-[10px]">
        <Icon className={`w-3.5 h-3.5 ${accent}`} /> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${accent}`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

/* ── PageSpeed card ───────────────────────────────────── */
function PageSpeedCard({ label, data }) {
  if (!data) return (
    <div className="border border-border p-3 text-xs text-muted-foreground">
      <div className="label-eyebrow mb-1">{label}</div>
      Not fetched yet.
    </div>
  );
  const s = data.scores || {};
  const c = data.core_web_vitals || {};
  const chip = (v) => v == null ? "—" : v >= 80 ? `chip text-[10px] bg-emerald-500/15 text-emerald-700 dark:text-emerald-300` : v >= 50 ? `chip text-[10px] bg-amber-500/15 text-amber-700 dark:text-amber-300` : `chip text-[10px] bg-red-500/15 text-red-700 dark:text-red-300`;
  return (
    <div className="border border-border p-3 text-xs" data-testid={`ps-${label.toLowerCase()}`}>
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="grid grid-cols-4 gap-1">
        <div><div className="text-[9px] text-muted-foreground">Perf</div><div className={s.performance != null ? chip(s.performance) : ""}>{s.performance ?? "—"}</div></div>
        <div><div className="text-[9px] text-muted-foreground">SEO</div><div className={s.seo != null ? chip(s.seo) : ""}>{s.seo ?? "—"}</div></div>
        <div><div className="text-[9px] text-muted-foreground">A11y</div><div className={s.accessibility != null ? chip(s.accessibility) : ""}>{s.accessibility ?? "—"}</div></div>
        <div><div className="text-[9px] text-muted-foreground">BP</div><div className={s.best_practices != null ? chip(s.best_practices) : ""}>{s.best_practices ?? "—"}</div></div>
      </div>
      <div className="grid grid-cols-3 gap-1 mt-2 text-[10px] text-muted-foreground">
        <span>LCP {c.lcp_ms ? `${(c.lcp_ms / 1000).toFixed(1)}s` : "—"}</span>
        <span>CLS {c.cls != null ? c.cls.toFixed(3) : "—"}</span>
        <span>INP {c.inp_ms ? `${Math.round(c.inp_ms)}ms` : "—"}</span>
      </div>
    </div>
  );
}

function RunPageSpeed({ prop, onDone, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await Promise.all([
        api.post(`/seo-properties/${prop.id}/pagespeed`, { url: prop.root_url, strategy: "mobile" }),
        api.post(`/seo-properties/${prop.id}/pagespeed`, { url: prop.root_url, strategy: "desktop" }),
      ]);
      toast.success("PageSpeed refreshed");
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "PageSpeed failed")); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={run} disabled={busy} data-testid={`${testid}-ps-refresh`}>
      {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCcw className="w-3 h-3 mr-1" />}
      Refresh
    </Button>
  );
}

/* ── Property setup dialog ───────────────────────────── */
function PropertySetupDialog({ workspaceId, brand, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    root_url: existing?.root_url || "",
    country: existing?.country || "IN",
    language: existing?.language || "en",
    target_keywords: (existing?.target_keywords || []).join(", "),
    gsc_property_url: existing?.gsc_property_url || "",
    ga4_property_id: existing?.ga4_property_id || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.root_url.trim()) { toast.error("Root URL required"); return; }
    setBusy(true);
    try {
      const payload = {
        root_url: form.root_url.trim(),
        country: form.country || "IN",
        language: form.language || "en",
        target_keywords: form.target_keywords.split(",").map((s) => s.trim()).filter(Boolean),
        gsc_property_url: form.gsc_property_url || null,
        ga4_property_id: form.ga4_property_id || null,
      };
      if (existing) {
        await api.patch(`/seo-properties/${existing.id}`, payload);
      } else {
        await api.post(`/workspaces/${workspaceId}/brands/${brand.id}/seo-property`, payload);
      }
      toast.success("SEO property saved");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="seo-setup-dialog">
        <DialogHeader><DialogTitle>{existing ? "Edit SEO property" : "Set up SEO for this brand"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Root URL *</Label>
            <Input value={form.root_url} onChange={(e) => upd("root_url", e.target.value)} placeholder="https://acmefoods.com" className="h-10 rounded-none" data-testid="seo-setup-root-url" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Country</Label>
              <Select value={form.country} onValueChange={(v) => upd("country", v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="seo-setup-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["IN","US","GB","AE","SG","AU","CA","DE","FR","IT","ES","BR","JP","ZA"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Language</Label>
              <Select value={form.language} onValueChange={(v) => upd("language", v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="seo-setup-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["en","hi","es","de","fr","it","pt","zh","ja","ar"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Target keywords (comma-separated)</Label>
            <Input value={form.target_keywords} onChange={(e) => upd("target_keywords", e.target.value)} placeholder="organic honey, raw honey india, best honey" className="h-10 rounded-none text-xs" data-testid="seo-setup-kw" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">GSC property URL</Label>
              <Input value={form.gsc_property_url} onChange={(e) => upd("gsc_property_url", e.target.value)} placeholder="sc-domain:acmefoods.com" className="h-10 rounded-none text-xs" data-testid="seo-setup-gsc" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">GA4 property ID</Label>
              <Input value={form.ga4_property_id} onChange={(e) => upd("ga4_property_id", e.target.value)} placeholder="properties/12345678" className="h-10 rounded-none text-xs" data-testid="seo-setup-ga4" />
            </div>
          </div>
          <div className="text-[11px] text-meta">
            GSC / GA4 connections require a Google OAuth setup — arriving in Phase 2. Store the IDs
            here now so the connection auto-links when OAuth ships.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="seo-setup-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── All-issues dialog with bulk convert-to-tasks ─────── */
function AllIssuesDialog({ run, onClose, testid }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [sev, setSev] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [convertOpen, setConvertOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [dueDays, setDueDays] = useState(14);
  const [converting, setConverting] = useState(false);

  const load = () => {
    if (!run?.id) return;
    setBusy(true);
    api.get(`/seo-audit-runs/${run.id}/issues${sev ? `?severity=${sev}` : ""}`)
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => {})
      .finally(() => setBusy(false));
  };
  useEffect(load, [run?.id, sev]);

  useEffect(() => {
    if (!run?.workspace_id) return;
    api.get(`/workspaces/${run.workspace_id}/projects`).then((r) => setProjects(r.data || [])).catch(() => {});
  }, [run?.workspace_id]);

  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(selected.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));

  const convert = async () => {
    if (!projectId) { toast.error("Pick a project"); return; }
    setConverting(true);
    try {
      const { data } = await api.post(`/seo-properties/${run.property_id}/issues-to-tasks`, {
        issue_ids: Array.from(selected),
        project_id: projectId,
        due_days_offset: Number(dueDays) || 14,
      });
      toast.success(`Created ${data.created} task(s) in ${data.project_name}`);
      setConvertOpen(false);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Convert failed")); }
    finally { setConverting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl" data-testid={`${testid}-all-issues-dialog`}>
        <DialogHeader><DialogTitle>Audit issues · {rows.length}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 mb-2">
          <Select value={sev || "_all"} onValueChange={(v) => setSev(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid={`${testid}-issues-sev-filter`}><SelectValue placeholder="All severities" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All severities</SelectItem>
              {["critical","high","medium","low"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-[11px] text-meta">{selected.size} selected</span>
          {selected.size > 0 && (
            <Button size="sm" className="rounded-none h-8 ml-auto" onClick={() => setConvertOpen(true)} data-testid={`${testid}-convert-tasks-btn`}>
              <ListChecks className="w-3.5 h-3.5 mr-1.5" /> Convert {selected.size} → tasks
            </Button>
          )}
        </div>
        <div className="border border-border max-h-[520px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                <th className="p-2 w-8"><input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleAll} data-testid={`${testid}-issues-select-all`} /></th>
                <th className="p-2 text-left w-24">Severity</th>
                <th className="p-2 text-left w-56">Kind</th>
                <th className="p-2 text-left">Page</th>
                <th className="p-2 text-left">Detail</th>
              </tr>
            </thead>
            <tbody>
              {busy && <tr><td colSpan={5} className="p-6 text-center text-meta">Loading…</td></tr>}
              {!busy && rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-meta">No issues match.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className={`border-b border-border ${r.converted_to_task_at ? "opacity-50" : ""}`} data-testid={`${testid}-issue-${r.id}`}>
                  <td className="p-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} disabled={!!r.converted_to_task_at} data-testid={`${testid}-issue-check-${r.id}`} /></td>
                  <td className="p-2"><span className={`chip text-[9px] border ${SEVERITY_STYLE[r.severity] || ""}`}>{r.severity}</span></td>
                  <td className="p-2 font-mono">{r.kind}{r.converted_to_task_at ? <span className="ml-1 text-[9px] text-emerald-600">✓ task</span> : null}</td>
                  <td className="p-2 max-w-[240px]"><a href={r.page_url} target="_blank" rel="noopener noreferrer" className="underline truncate block">{r.page_url}</a></td>
                  <td className="p-2 text-muted-foreground">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
      {convertOpen && (
        <Dialog open onOpenChange={(v) => { if (!v) setConvertOpen(false); }}>
          <DialogContent className="max-w-md" data-testid={`${testid}-convert-dialog`}>
            <DialogHeader><DialogTitle>Convert {selected.size} issue(s) → project tasks</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Target project *</Label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger className="h-10 rounded-none" data-testid={`${testid}-convert-project`}><SelectValue placeholder="Pick a project" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Due in days</Label>
                <Input type="number" value={dueDays} onChange={(e) => setDueDays(e.target.value)} className="h-10 rounded-none" data-testid={`${testid}-convert-due`} />
              </div>
              <p className="text-[11px] text-meta">Each task&apos;s priority auto-maps from severity (critical→urgent, high→high, medium→medium, low→low). Converted issues stay in the audit but are marked as ✓ task.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setConvertOpen(false)}>Cancel</Button>
              <Button className="rounded-none" onClick={convert} disabled={converting || !projectId} data-testid={`${testid}-convert-submit`}>
                {converting ? "Creating…" : "Create tasks"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

/* ── Sub-tabs: Overview / Backlinks / Keywords / Progress ───── */
function SubTabs({ prop, testid }) {
  const [tab, setTab] = useState("overview");
  return (
    <div>
      <div className="flex border-b border-border" data-testid={`${testid}-subtabs`}>
        {[
          { v: "overview", label: "Overview", icon: TrendingUp },
          { v: "backlinks", label: "Backlinks", icon: Link2 },
          { v: "keywords", label: "Keywords", icon: Key },
          { v: "progress", label: "Progress", icon: Zap },
        ].map(({ v, label, icon: Ic }) => (
          <button key={v} onClick={() => setTab(v)}
                  className={`px-4 py-2 text-xs inline-flex items-center gap-1.5 border-b-2 ${tab === v ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  data-testid={`${testid}-subtab-${v}`}>
            <Ic className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>
      <div className="pt-4">
        {tab === "backlinks" && <BacklinksTab prop={prop} testid={testid} />}
        {tab === "keywords" && <KeywordsTab prop={prop} testid={testid} />}
        {tab === "progress" && <ProgressTab prop={prop} testid={testid} />}
      </div>
    </div>
  );
}

function BacklinksTab({ prop, testid }) {
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ source_domain: "", target_url: "", anchor_text: "", link_type: "guest_post", status: "planned", da_manual: "", cost: "" });
  const [busy, setBusy] = useState(true);
  const load = () => { setBusy(true); api.get(`/seo-properties/${prop.id}/backlinks`).then((r) => setRows(r.data?.rows || [])).finally(() => setBusy(false)); };
  useEffect(load, [prop.id]);
  const save = async () => {
    if (!form.source_domain.trim()) { toast.error("Source domain required"); return; }
    try {
      await api.post(`/seo-properties/${prop.id}/backlinks`, {
        ...form,
        da_manual: form.da_manual ? Number(form.da_manual) : null,
        cost: form.cost ? Number(form.cost) : null,
      });
      toast.success("Backlink added");
      setAddOpen(false);
      setForm({ source_domain: "", target_url: "", anchor_text: "", link_type: "guest_post", status: "planned", da_manual: "", cost: "" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const patch = async (bid, k, v) => {
    try { await api.patch(`/seo-backlinks/${bid}`, { [k]: v }); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const groups = { planned: [], submitted: [], live: [], rejected: [], lost: [] };
  rows.forEach((r) => { (groups[r.status] || groups.planned).push(r); });
  return (
    <div className="space-y-4" data-testid={`${testid}-backlinks-tab`}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">{rows.length} backlinks · {groups.live.length} live</div>
        <Button size="sm" className="rounded-none" onClick={() => setAddOpen(true)} data-testid={`${testid}-backlink-add-btn`}>
          <Link2 className="w-3.5 h-3.5 mr-1.5" /> New backlink
        </Button>
      </div>
      <div className="grid md:grid-cols-5 gap-2">
        {Object.entries(groups).map(([status, items]) => (
          <div key={status} className="border border-border p-2" data-testid={`${testid}-bl-col-${status}`}>
            <div className="label-eyebrow mb-2 flex justify-between"><span>{status}</span><span className="text-meta">{items.length}</span></div>
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
              {items.map((r) => (
                <div key={r.id} className="border border-border p-2 text-[11px]" data-testid={`${testid}-bl-${r.id}`}>
                  <div className="font-semibold truncate">{r.source_domain}</div>
                  {r.anchor_text && <div className="text-meta truncate">&ldquo;{r.anchor_text}&rdquo;</div>}
                  <div className="flex items-center gap-1 mt-1 text-[10px]">
                    <span className="chip text-[9px]">{r.link_type}</span>
                    {r.da_manual != null && <span className="chip text-[9px]">DA {r.da_manual}</span>}
                    {r.cost != null && <span className="chip text-[9px]">${r.cost}</span>}
                  </div>
                  <Select value={r.status} onValueChange={(v) => patch(r.id, "status", v)}>
                    <SelectTrigger className="h-6 text-[10px] mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{["planned","submitted","live","rejected","lost"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {busy && <div className="text-xs text-meta">Loading…</div>}
      {addOpen && (
        <Dialog open onOpenChange={(v) => { if (!v) setAddOpen(false); }}>
          <DialogContent className="max-w-md" data-testid={`${testid}-bl-add-dialog`}>
            <DialogHeader><DialogTitle>New backlink</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <Input placeholder="Source domain *" value={form.source_domain} onChange={(e) => setForm({ ...form, source_domain: e.target.value })} className="h-9 rounded-none text-xs" data-testid={`${testid}-bl-src-domain`} />
              <Input placeholder="Target URL" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} className="h-9 rounded-none text-xs" data-testid={`${testid}-bl-target`} />
              <Input placeholder="Anchor text" value={form.anchor_text} onChange={(e) => setForm({ ...form, anchor_text: e.target.value })} className="h-9 rounded-none text-xs" data-testid={`${testid}-bl-anchor`} />
              <div className="grid grid-cols-2 gap-2">
                <Select value={form.link_type} onValueChange={(v) => setForm({ ...form, link_type: v })}>
                  <SelectTrigger className="h-9 rounded-none text-xs" data-testid={`${testid}-bl-type`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["guest_post","directory","pr","partnership","resource_page","forum","profile","other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="h-9 rounded-none text-xs" data-testid={`${testid}-bl-status`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["planned","submitted","live","rejected","lost"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" placeholder="DA (0-100)" value={form.da_manual} onChange={(e) => setForm({ ...form, da_manual: e.target.value })} className="h-9 rounded-none text-xs" />
                <Input type="number" placeholder="Cost" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="h-9 rounded-none text-xs" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button className="rounded-none" onClick={save} data-testid={`${testid}-bl-save`}>Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function KeywordsTab({ prop, testid }) {
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ keyword: "", bucket: "seed", priority: "stretch", target_url: "", search_volume: "", difficulty: "", current_position: "" });
  const [busy, setBusy] = useState(true);
  const load = () => { setBusy(true); api.get(`/seo-properties/${prop.id}/keywords`).then((r) => setRows(r.data?.rows || [])).finally(() => setBusy(false)); };
  useEffect(load, [prop.id]);
  const save = async () => {
    if (!form.keyword.trim()) { toast.error("Keyword required"); return; }
    try {
      await api.post(`/seo-properties/${prop.id}/keywords`, {
        ...form,
        search_volume: form.search_volume ? Number(form.search_volume) : null,
        difficulty: form.difficulty ? Number(form.difficulty) : null,
        current_position: form.current_position ? Number(form.current_position) : null,
      });
      toast.success("Keyword added");
      setAddOpen(false);
      setForm({ keyword: "", bucket: "seed", priority: "stretch", target_url: "", search_volume: "", difficulty: "", current_position: "" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  return (
    <div className="space-y-4" data-testid={`${testid}-keywords-tab`}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">{rows.length} keywords · Phase 2 unlocks auto-enrichment via DataForSEO</div>
        <Button size="sm" className="rounded-none" onClick={() => setAddOpen(true)} data-testid={`${testid}-kw-add-btn`}>
          <Key className="w-3.5 h-3.5 mr-1.5" /> New keyword
        </Button>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 border-b border-border">
            <tr>
              <th className="p-2 text-left">Keyword</th>
              <th className="p-2 text-left w-24">Bucket</th>
              <th className="p-2 text-left w-24">Priority</th>
              <th className="p-2 text-left w-20 text-right">Volume</th>
              <th className="p-2 text-left w-20 text-right">Diff.</th>
              <th className="p-2 text-left w-20 text-right">Position</th>
              <th className="p-2 text-left">Target</th>
            </tr>
          </thead>
          <tbody>
            {busy && <tr><td colSpan={7} className="p-6 text-center text-meta">Loading…</td></tr>}
            {!busy && rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-meta">No keywords yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border hover:bg-muted/30" data-testid={`${testid}-kw-${r.id}`}>
                <td className="p-2 font-medium">{r.keyword}</td>
                <td className="p-2"><span className="chip text-[10px]">{r.bucket}</span></td>
                <td className="p-2"><span className="chip text-[10px]">{r.priority}</span></td>
                <td className="p-2 text-right tabular-nums">{r.search_volume ?? "—"}</td>
                <td className="p-2 text-right tabular-nums">{r.difficulty ?? "—"}</td>
                <td className="p-2 text-right tabular-nums">{r.current_position ?? "—"}</td>
                <td className="p-2 max-w-[220px] truncate">{r.target_url || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {addOpen && (
        <Dialog open onOpenChange={(v) => { if (!v) setAddOpen(false); }}>
          <DialogContent className="max-w-md" data-testid={`${testid}-kw-add-dialog`}>
            <DialogHeader><DialogTitle>New keyword</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <Input placeholder="Keyword *" value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} className="h-9 rounded-none" data-testid={`${testid}-kw-input`} />
              <div className="grid grid-cols-2 gap-2">
                <Select value={form.bucket} onValueChange={(v) => setForm({ ...form, bucket: v })}>
                  <SelectTrigger className="h-9 rounded-none text-xs" data-testid={`${testid}-kw-bucket`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["seed","long_tail","brand","competitor","question","local"].map((s) => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="h-9 rounded-none text-xs" data-testid={`${testid}-kw-priority`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["must_rank","stretch","experiment"].map((s) => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Target URL" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} className="h-9 rounded-none text-xs col-span-2" />
                <Input type="number" placeholder="Search volume" value={form.search_volume} onChange={(e) => setForm({ ...form, search_volume: e.target.value })} className="h-9 rounded-none text-xs" />
                <Input type="number" placeholder="Difficulty (0-100)" value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="h-9 rounded-none text-xs" />
                <Input type="number" placeholder="Current position" value={form.current_position} onChange={(e) => setForm({ ...form, current_position: e.target.value })} className="h-9 rounded-none text-xs col-span-2" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button className="rounded-none" onClick={save} data-testid={`${testid}-kw-save`}>Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ProgressTab({ prop, testid }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    api.get(`/seo-properties/${prop.id}/progress-report?days=${days}`).then((r) => setData(r.data)).catch(() => {});
  }, [prop.id, days]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (!data) return <div className="text-xs text-muted-foreground">Loading progress report…</div>;
  return (
    <div className="space-y-4" data-testid={`${testid}-progress-tab`}>
      <div className="flex items-center gap-2">
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid={`${testid}-progress-range`}><SelectValue /></SelectTrigger>
          <SelectContent>{[7, 14, 30, 60, 90, 180].map((n) => <SelectItem key={n} value={String(n)}>{n} days</SelectItem>)}</SelectContent>
        </Select>
        <a href={`${backendUrl}/api/v1/seo-properties/${prop.id}/progress-report/pdf?days=${days}`}
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center h-8 px-3 border border-input text-xs hover:bg-muted rounded-none ml-auto"
             data-testid={`${testid}-progress-download`}>
          <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={TrendingUp} label={`Health Δ · ${days}d`} value={data.delta_health >= 0 ? `+${data.delta_health}` : data.delta_health}
                 accent={data.delta_health > 0 ? "text-emerald-600" : data.delta_health < 0 ? "text-red-600" : "text-foreground"} />
        <KpiCard icon={AlertTriangle} label={`Issues Δ · ${days}d`} value={data.delta_issues >= 0 ? `+${data.delta_issues}` : data.delta_issues}
                 accent={data.delta_issues < 0 ? "text-emerald-600" : data.delta_issues > 0 ? "text-red-600" : "text-foreground"} />
        <KpiCard icon={ListChecks} label="SEO tasks closed" value={data.seo_tasks_closed} accent="text-emerald-600" />
        <KpiCard icon={ListChecks} label="SEO tasks open" value={data.seo_tasks_open} />
        <KpiCard icon={Link2} label="Backlinks added" value={data.backlinks_added} accent="text-emerald-600" />
        <KpiCard icon={Link2} label="Backlinks live" value={data.backlinks_live} />
        <KpiCard icon={Link2} label="Backlinks lost" value={data.backlinks_lost} accent={data.backlinks_lost > 0 ? "text-red-600" : ""} />
        <KpiCard icon={Key} label="Keywords tracked" value={data.keywords_total} />
      </div>
      <section className="border border-border p-4">
        <div className="label-eyebrow mb-3">Audit history · sparkline</div>
        {data.runs_series.length === 0 && <div className="text-xs text-muted-foreground">No audits in this range.</div>}
        <div className="flex items-end gap-1 h-24">
          {data.runs_series.map((r) => (
            <div key={r.id} className="flex-1 bg-primary/30 hover:bg-primary transition"
                  style={{ height: `${r.health_score || 0}%` }}
                  title={`${r.health_score}/100 · ${r.pages_crawled} pages · ${(r.finished_at || "").slice(0, 10)}`} />
          ))}
        </div>
      </section>
    </div>
  );
}
